import { useCallback, useContext, useMemo } from 'react';
import { OkButton } from 'src/components/Buttons/OkButton';
import { MarketplacePaymentStatePill } from 'src/components/common/MarketplacePaymentStatePill/MarketplacePaymentStatePill';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserCanUpdateSale } from 'src/hooks/useUserHasSalePermissions';
import { Detail } from 'src/modals/common';
import { getSalesGroupModalConfigWithDeepLink } from 'src/modals/SalesGroup';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKETPLACE_PAYMENT_TERM_TO_CID } from 'src/utils/constants/contentIdMaps';
import { formatCurrency } from 'src/utils/numberFormatter';
import {
  Feature,
  Marketplace,
  MarketplacePaymentState,
  MarketplacePaymentTerm,
  SaleDetails,
} from 'src/WebApiController';

import { GroupMarketplacePaymentDialog } from './GroupMarketplacePaymentDialog';
import {
  canAddSalePayment,
  getSaleOutstandingAmt,
} from './MarketplacePaymentDialog.utils';

interface MarketplacePaymentTermsProps {
  marketplacePaymentTerm: MarketplacePaymentTerm | null | undefined;
  marketplacePaymentState: MarketplacePaymentState | null | undefined;
  sale?: SaleDetails | null;
}

export const MarketplacePaymentTerms = ({
  marketplacePaymentTerm,
  marketplacePaymentState,
  sale,
}: MarketplacePaymentTermsProps) => {
  const { setModal, currentModalConfig } = useContext(ModalContext);
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);
  const canUpdate = useUserCanUpdateSale(sale, false /* isBulkEdit */);

  const outstandingAmount = useMemo(
    () => getSaleOutstandingAmt(sale?.mkpLines),
    [sale?.mkpLines]
  );

  const openAddPayment = useCallback(() => {
    if (!sale) {
      return;
    }
    setModal({
      children: (
        <GroupMarketplacePaymentDialog
          saleId={sale.id}
          remainingAmount={outstandingAmount}
          currencyCode={sale.currency ?? 'USD'}
          buyerEmail={sale.transDest?.email ?? null}
          cancelTo={currentModalConfig}
          // SaleDetails is driven by ActivePosEntityContext (not the dialog's query-key
          // invalidation), so reload the active entity on success to reflect the payment.
          onPaymentSuccess={() => {
            setActivePosEntity(sale.id, sale.idOnMkp, true);
          }}
        />
      ),
    });
  }, [
    sale,
    setModal,
    outstandingAmount,
    currentModalConfig,
    setActivePosEntity,
  ]);

  const openSalesGroup = useCallback(() => {
    if (sale?.salesGroupId == null) {
      return;
    }
    setModal(getSalesGroupModalConfigWithDeepLink(sale.salesGroupId));
  }, [sale?.salesGroupId, setModal]);

  const isRetailSale = hasRetailSaleFeature && sale?.mkp === Marketplace.Retail;

  const canPayOutstanding = useMemo(() => {
    if (!hasRetailSaleFeature || sale?.mkp !== Marketplace.Retail) {
      return false;
    }

    return canAddSalePayment({
      isRetail: true,
      outstandingAmt: outstandingAmount,
      canUpdateSale: canUpdate,
    });
  }, [canUpdate, hasRetailSaleFeature, outstandingAmount, sale?.mkp]);

  if (sale == null || !marketplacePaymentTerm || !marketplacePaymentState) {
    return null;
  }

  return (
    <Stack width="full" justifyContent="spaceBetween">
      <Stack gap="m">
        <Detail
          label={<Content id={ContentId.MarketplaceTerms} />}
          detail={
            <Content
              id={MARKETPLACE_PAYMENT_TERM_TO_CID[marketplacePaymentTerm]}
            />
          }
        />
        <Detail
          label={<Content id={ContentId.PaymentState} />}
          detail={
            <MarketplacePaymentStatePill state={marketplacePaymentState} />
          }
        />
      </Stack>
      {canPayOutstanding && (
        <Stack gap="m" alignItems="center">
          <Detail
            label={<Content id={ContentId.OutstandingBalance} />}
            detail={formatCurrency(outstandingAmount, sale.currency)}
          />
          {sale?.salesGroupId != null ? (
            <OkButton
              textContentId={ContentId.PayInGroup}
              variant="link"
              onClick={openSalesGroup}
            />
          ) : (
            <OkButton
              textContentId={ContentId.AddPayment}
              variant="outlineBrand"
              onClick={openAddPayment}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
